import React, { useEffect, useState } from "react";
import {
  fetchParties,
  fetchPreviousDayLedger,
  fetchTodayOrderAmount,
  saveDayClose
} from "../services/cashierService";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";

const ONLINE_COLLECTION_URL ="https://script.google.com/macros/s/AKfycbyoUbeqL_LwQVRd2KOAE-8-2cpMNyYaVuhUoRkZQSKSMOvZrCpt5Y6h03n-gvmGs7Pn5Q/exec";

const fmt = (v) => Number(v || 0).toFixed(2);

async function getServerISTTime(db) {
  const ref = doc(db, "server_time", "now");
  await setDoc(ref, { time: serverTimestamp() });
  let snap;
  let serverTime = null;
  for (let i = 0; i < 5; i++) {
    snap = await getDoc(ref);
    if (snap.exists() && snap.data().time) {
      serverTime = snap.data().time.toDate();
      break;
    }
    await new Promise(r => setTimeout(r, 200));
  }
  if (!serverTime) throw new Error("Server time fetch failed");
  const utc = serverTime.getTime() + (serverTime.getTimezoneOffset() * 60000);
  const ist = new Date(utc + (5.5 * 60 * 60 * 1000));
  return ist;
}

export default function Cashier() {
  const navigate = useNavigate();
  const [timeBlocked, setTimeBlocked] = React.useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const checkServerTime = async () => {
        try {
          const istTime = await getServerISTTime(db);
          const hours = istTime.getHours();
          const minutes = istTime.getMinutes();
          const currentMinutes = (hours * 60) + minutes;
          const start = (9 * 60) + 45;   // 09:45 AM
          const end = (13 * 60) + 30;    // 01:30 PM
          if (currentMinutes < start || currentMinutes > end) {
            setTimeBlocked(true);
            return;
          }
          setTimeBlocked(false);
        } catch (e) {
          console.error("Time check failed", e);
        }
      };
      checkServerTime();
    });
    return () => unsub();
  }, [navigate]);

  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [onlineCollectionMap, setOnlineCollectionMap] = useState({});
  const [onlineCollected, setOnlineCollected] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(null);
  const [orderAmount, setOrderAmount] = useState(0);
  const [DENOM_ORDER] = useState([500, 200, 100, 50, 20, 10, 5, 1]);
  const [denoms, setDenoms] = useState({
    500: "", 200: "", 100: "", 50: "", 20: "", 10: "", 5: "", 1: ""
  });
  const [cashTotal, setCashTotal] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchParties().then(setParties);
  }, []);

  useEffect(() => {
    if (!partyId) return;
    const today = new Date().toISOString().slice(0, 10);
    fetchPreviousDayLedger(partyId, today).then(prev => {
      setOpeningBalance(prev?.closingBalance ?? 0);
      setBlocked(false);
    });
    fetchTodayOrderAmount(partyId, today).then(setOrderAmount);
  }, [partyId]);

  useEffect(() => {
    if (openingBalance === null) return;
    let cash = 0;
    DENOM_ORDER.forEach(d => {
      cash += (denoms[d] || 0) * d;
    });
    setCashTotal(cash);
    setClosingBalance(
      Number(openingBalance) +
      Number(orderAmount) -
      Number(onlineCollected) -
      Number(cash)
    );
  }, [denoms, openingBalance, orderAmount, onlineCollected, DENOM_ORDER]);

  useEffect(() => {
    async function fetchOnlineCollection() {
      try {
        const res = await fetch(ONLINE_COLLECTION_URL);
        const data = await res.json();
        setOnlineCollectionMap(data || {});
      } catch (err) {
        console.error("Online collection fetch failed", err);
        setOnlineCollectionMap({});
      }
    }
    fetchOnlineCollection();
  }, []);

  useEffect(() => {
    if (!partyId) {
      setOnlineCollected(0);
      return;
    }
    const amount = Number(onlineCollectionMap[partyId] || 0);
    setOnlineCollected(amount);
  }, [partyId, onlineCollectionMap]);

  const submit = async () => {
    if (blocked || submitted) return;
    await saveDayClose({
      partyId,
      openingBalance,
      orderAmount,
      denominations: denoms,
      onlineCollected,
      cashCollected: cashTotal
    });
    setPreview({
      partyId,
      openingBalance,
      orderAmount,
      cashTotal,
      onlineCollected,
      closingBalance
    });
    setSubmitted(true);
  };

  if (timeBlocked) {
    return (
      <Layout>
        <div className="layout-content" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
          <div className="premium-card" style={{ maxWidth: 400, padding: 40, textAlign: "center", border: "1px solid var(--danger)" }}>
            <div style={{ fontSize: 60, marginBottom: 24 }}>🛑</div>
            <h2 style={{ color: "var(--danger)", fontSize: 28, fontWeight: 900, marginBottom: 12 }}>Terminal Locked</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 16, marginBottom: 32 }}>Security protocols restrict cashier operations beyond authorized fiscal windows.</p>
            <div style={{ background: "var(--surface-hover)", padding: 20, borderRadius: 16, marginBottom: 32 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8 }}>Authorized Window</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "var(--text-main)" }}>09:45 AM — 01:30 PM</div>
            </div>
            <button onClick={() => navigate("/operator")} className="btn-secondary" style={{ width: "100%", padding: 14 }}>Return to Command Center</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="layout-content" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{ maxWidth: 500, width: "100%", marginBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <h1 style={{ fontSize: 28, margin: 0, fontWeight: 900 }}>Cash Terminal</h1>
                    <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>Liquidate today's receipts into system ledger.</p>
                </div>
                <button onClick={() => navigate("/operator")} className="btn-secondary" style={{ padding: "8px 16px", fontSize: 12 }}>Exit</button>
            </div>
        </div>

        <div className="premium-card" style={{ maxWidth: 500, width: "100%", padding: 32 }}>
          {!submitted ? (
            <>
                <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" }}>Target Party Node</label>
                    <select
                      className="form-input"
                      value={partyId}
                      onChange={e => setPartyId(e.target.value)}
                      style={{ width: "100%", padding: 16, fontSize: 18, fontWeight: 700 }}
                    >
                      <option value="">-- SELECT AGENT --</option>
                      {parties.map(p => (
                        <option key={p.partyId} value={p.partyId}>{p.name}</option>
                      ))}
                    </select>
                </div>

                {openingBalance !== null && !blocked && (
                    <div style={{ animation: "fadeIn 0.3s ease-out" }}>
                      <div style={{ background: "var(--surface-hover)", padding: 24, borderRadius: 20, marginBottom: 32, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Opening Base</div>
                            <div style={{ fontSize: 20, fontWeight: 800 }}>₹{fmt(openingBalance)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Order Gross</div>
                            <div style={{ fontSize: 20, fontWeight: 800 }}>₹{fmt(orderAmount)}</div>
                        </div>
                        <div style={{ gridColumn: "span 2", paddingTop: 12, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--success)" }}>BHARAT-PE / ONLINE</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--success)" }}>₹{fmt(onlineCollected)}</div>
                        </div>
                      </div>

                      <h3 style={{ fontSize: 14, fontWeight: 800, marginBottom: 20 }}>CASH DENOMINATIONS</h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
                        {DENOM_ORDER.map(d => (
                            <div key={d} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                <div style={{ width: 60, fontSize: 16, fontWeight: 800, color: "var(--text-muted)" }}>₹{d}</div>
                                <div style={{ fontSize: 16, color: "var(--border)" }}>×</div>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="0"
                                    value={denoms[d]}
                                    onChange={e => setDenoms({ ...denoms, [d]: Number(e.target.value) })}
                                    style={{ flex: 1, textAlign: "right", fontWeight: 800, fontSize: 18, padding: "10px 16px" }}
                                />
                                <div style={{ width: 100, textAlign: "right", fontSize: 16, fontWeight: 800, color: "var(--text-main)" }}>
                                    ₹{fmt((denoms[d] || 0) * d)}
                                </div>
                            </div>
                        ))}
                      </div>

                      <div style={{ borderTop: "2px solid var(--border)", paddingTop: 24, marginBottom: 32 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-muted)" }}>CASH TOTAL</span>
                            <span style={{ fontSize: 24, fontWeight: 900 }}>₹{fmt(cashTotal)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px", borderRadius: 16, background: "rgba(247, 70, 5, 0.05)", border: "1px solid rgba(247, 70, 5, 0.1)" }}>
                            <span style={{ fontSize: 16, fontWeight: 900, color: "#f74605" }}>TERMINAL BALANCE</span>
                            <span style={{ fontSize: 28, fontWeight: 900, color: "#f74605" }}>₹{fmt(closingBalance)}</span>
                        </div>
                      </div>

                      <button
                        onClick={submit}
                        className="btn-primary"
                        style={{ width: "100%", padding: 20, fontSize: 16, fontWeight: 900, borderRadius: 16, boxShadow: "0 10px 30px -10px rgba(255, 107, 0, 0.5)" }}
                      >
                        ⚡ AUTHORIZE FISCAL CLOSURE
                      </button>
                    </div>
                )}
            </>
          ) : (
            <div style={{ animation: "fadeIn 0.3s ease-out", textAlign: "center" }}>
                <div style={{ fontSize: 60, marginBottom: 24 }}>✅</div>
                <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>Fiscal Ingestion Verified</h2>
                <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 32 }}>Ledger entry for {preview.partyId} has been successfully persisted.</p>
                
                <div style={{ background: "var(--surface-hover)", padding: 24, borderRadius: 24, textAlign: "left", marginBottom: 32 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Opening Liquidity</span>
                            <span style={{ fontWeight: 800 }}>₹{fmt(preview.openingBalance)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Inbound Sales</span>
                            <span style={{ fontWeight: 800 }}>₹{fmt(preview.orderAmount)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Online Collection</span>
                            <span style={{ fontWeight: 800 }}>₹{fmt(preview.onlineCollected)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                            <span style={{ color: "var(--text-main)", fontWeight: 800 }}>Cash Remitted</span>
                            <span style={{ fontWeight: 800 }}>₹{fmt(preview.cashTotal)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span style={{ color: "#f74605", fontWeight: 900 }}>Stationary Balance</span>
                            <span style={{ color: "#f74605", fontWeight: 900, fontSize: 20 }}>₹{fmt(preview.closingBalance)}</span>
                        </div>
                    </div>
                </div>

                <button onClick={() => setSubmitted(false)} className="btn-secondary" style={{ width: "100%", padding: 14 }}>Authorise Next Entity</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
